import { defModule, Task, type TaskDeclaration, type TaskMessage } from '@st/redux'
import { match, P } from '@st/pattern'
import type { UserState } from '@st/sdk'
import { Stream } from 'effect'
import { query } from '~/effects'
import { observeCurrentUser } from '~/features/module-runtime'

export type UserConnectionStatus = UserState['connectionStatus']

export type UserStateModuleState =
  | { status: 'inactive' }
  | { status: 'loading'; currentUserId: string }
  | { status: 'loaded'; currentUserId: string; state: UserState }

type UserStateMessage =
  | TaskMessage<typeof observeCurrentUser>
  | { type: 'stateLoad'; state: UserState }

const observeUserState = Task.define((_currentUserId: string) =>
  query({ type: 'accounts/getUserState' }).pipe(
    Stream.map((state): UserStateMessage => ({ type: 'stateLoad', state })),
    Stream.orDie
  )
)

export const userStateModule = defModule<{
  state: UserStateModuleState
  message: UserStateMessage
}>({
  init: () => ({ status: 'inactive' }),
  tasks: (state) => [observeCurrentUser(), ...userStateTasks(state)],
  update: (state, message): UserStateModuleState => {
    switch (message.type) {
      case 'currentUserChanged':
        if (state.status != 'inactive' && state.currentUserId == message.currentUserId) {
          return state
        }
        if (state.status == 'inactive' && message.currentUserId == null) return state

        if (message.currentUserId == null) return { status: 'inactive' }
        return { status: 'loading', currentUserId: message.currentUserId }

      case 'stateLoad': {
        if (state.status == 'inactive') {
          throw new Error('User state loaded without an active user')
        }

        return { status: 'loaded', currentUserId: state.currentUserId, state: message.state }
      }
    }
  }
})

export function selUserConnectionStatus(state: UserStateModuleState): UserConnectionStatus {
  return match(state)
    .with({ status: 'loaded' }, ({ state }) => state.connectionStatus)
    .with({ status: P.union('inactive', 'loading') }, () => 'disconnected' as const)
    .exhaustive()
}

function userStateTasks(state: UserStateModuleState): TaskDeclaration<UserStateMessage>[] {
  if (state.status == 'inactive') return []

  return [observeUserState(state.currentUserId)]
}
